import * as React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Skeleton,
} from '@mui/material';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import PaymentsIcon from '@mui/icons-material/Payments';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import OutboxIcon from '@mui/icons-material/Outbox';
import { withTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import MetricCard from '../components/MetricCard';
import ExportButtons from '../components/ExportButtons';
import { useCashRegisterReport } from '../hooks/useReportsApi';

const CASH_COLOR = '#10b981';
const CARD_COLOR = '#3182ce';
const BANK_TRANSFER_COLOR = '#f59e0b';
const REFUND_BANK_COLOR = '#ef4444';
const INVOICED_ELSEWHERE_COLOR = '#8b5cf6';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
};

function CashRegisterSection({ t, dateParams }) {
  const { data, loading, error } = useCashRegisterReport(dateParams);

  if (error) {
    return <Alert severity="error">{t('reports.error')}: {error}</Alert>;
  }

  // Format chart X-axis labels based on number of days
  const chartData = (data?.daily_breakdown || []).map((item) => {
    const dateParts = item.date.split('-');
    const shortDate = `${parseInt(dateParts[2])}.${parseInt(dateParts[1])}`;
    return {
      ...item,
      label: data?.daily_breakdown.length <= 14
        ? `${item.day_name} ${shortDate}`
        : shortDate,
    };
  });

  // Bank-paid refunds are rare, so the column only appears for periods that actually have one
  // rather than sitting permanently empty on every workshop's report.
  const hasBankRefunds = parseFloat(data?.refund_bank_total || 0) !== 0;
  // Sales billed through another system (Laskutettu muualla): same rule, own column only when present
  const hasInvoicedElsewhere = parseFloat(data?.invoiced_elsewhere_total || 0) !== 0;
  const extraColumns = (hasBankRefunds ? 1 : 0) + (hasInvoicedElsewhere ? 1 : 0);

  return (
    <Box>
      {/* Export Buttons */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <ExportButtons reportType="cash-register" params={dateParams} />
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={PointOfSaleIcon}
            label={t('reports.cashRegister.totalSales')}
            value={data?.total_sales}
            loading={loading}
            format="currency"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={PaymentsIcon}
            label={t('reports.cashRegister.cash')}
            value={data?.cash_total}
            loading={loading}
            format="currency"
            iconColor="#10b981"
            iconBgColor="rgba(16, 185, 129, 0.1)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={CreditCardIcon}
            label={t('reports.cashRegister.card')}
            value={data?.card_total}
            loading={loading}
            format="currency"
            iconColor="#3182ce"
            iconBgColor="rgba(49, 130, 206, 0.1)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={AccountBalanceIcon}
            label={t('reports.cashRegister.bankTransfer')}
            value={data?.bank_transfer_total}
            loading={loading}
            format="currency"
            iconColor="#f59e0b"
            iconBgColor="rgba(245, 158, 11, 0.1)"
          />
        </Grid>
        {hasBankRefunds && (
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={AccountBalanceIcon}
              label={t('reports.cashRegister.refundBank')}
              value={data?.refund_bank_total}
              loading={loading}
              format="currency"
              iconColor="#ef4444"
              iconBgColor="rgba(239, 68, 68, 0.1)"
            />
          </Grid>
        )}
        {hasInvoicedElsewhere && (
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={OutboxIcon}
              label={t('reports.cashRegister.invoicedElsewhere')}
              value={data?.invoiced_elsewhere_total}
              loading={loading}
              format="currency"
              iconColor={INVOICED_ELSEWHERE_COLOR}
              iconBgColor="rgba(139, 92, 246, 0.1)"
            />
          </Grid>
        )}
      </Grid>

      {/* Stacked Bar Chart */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t('reports.cashRegister.dailyBreakdown')}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={350} />
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: chartData.length > 20 ? 10 : 12 }}
                angle={chartData.length > 14 ? -45 : 0}
                textAnchor={chartData.length > 14 ? 'end' : 'middle'}
                height={chartData.length > 14 ? 60 : 30}
              />
              <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}k €`} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Bar
                dataKey="cash"
                name={t('reports.cashRegister.cash')}
                fill={CASH_COLOR}
                stackId="a"
              />
              <Bar
                dataKey="card"
                name={t('reports.cashRegister.card')}
                fill={CARD_COLOR}
                stackId="a"
              />
              <Bar
                dataKey="bank_transfer"
                name={t('reports.cashRegister.bankTransfer')}
                fill={BANK_TRANSFER_COLOR}
                stackId="a"
              />
              {/* Without this the stacked bars would not add up to the Yhteensä column, since
                  refunds are part of the day's total. */}
              {hasBankRefunds && (
                <Bar
                  dataKey="refund_bank"
                  name={t('reports.cashRegister.refundBank')}
                  fill={REFUND_BANK_COLOR}
                  stackId="a"
                />
              )}
              {hasInvoicedElsewhere && (
                <Bar
                  dataKey="invoiced_elsewhere"
                  name={t('reports.cashRegister.invoicedElsewhere')}
                  fill={INVOICED_ELSEWHERE_COLOR}
                  stackId="a"
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">{t('reports.noData')}</Typography>
          </Box>
        )}
      </Paper>

      {/* Daily Summary Table */}
      <Paper elevation={0} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t('reports.cashRegister.dailyBreakdown')}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={200} />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('reports.cashRegister.date')}</TableCell>
                  <TableCell>{t('reports.cashRegister.day')}</TableCell>
                  <TableCell align="right">{t('reports.cashRegister.cash')}</TableCell>
                  <TableCell align="right">{t('reports.cashRegister.card')}</TableCell>
                  <TableCell align="right">{t('reports.cashRegister.bankTransfer')}</TableCell>
                  {hasBankRefunds && (
                    <TableCell align="right">{t('reports.cashRegister.refundBank')}</TableCell>
                  )}
                  {hasInvoicedElsewhere && (
                    <TableCell align="right">{t('reports.cashRegister.invoicedElsewhere')}</TableCell>
                  )}
                  <TableCell align="right">{t('reports.cashRegister.total')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.daily_breakdown || []).map((day, index) => {
                  const dateParts = day.date.split('-');
                  const displayDate = `${parseInt(dateParts[2])}.${parseInt(dateParts[1])}.${dateParts[0]}`;
                  return (
                    <TableRow key={index}>
                      <TableCell>{displayDate}</TableCell>
                      <TableCell>{day.day_name}</TableCell>
                      <TableCell align="right">{formatCurrency(day.cash)}</TableCell>
                      <TableCell align="right">{formatCurrency(day.card)}</TableCell>
                      <TableCell align="right">{formatCurrency(day.bank_transfer)}</TableCell>
                      {hasBankRefunds && (
                        <TableCell align="right">{formatCurrency(day.refund_bank)}</TableCell>
                      )}
                      {hasInvoicedElsewhere && (
                        <TableCell align="right">{formatCurrency(day.invoiced_elsewhere)}</TableCell>
                      )}
                      <TableCell align="right" sx={{ fontWeight: 500 }}>{formatCurrency(day.total)}</TableCell>
                    </TableRow>
                  );
                })}
                {(!data?.daily_breakdown || data.daily_breakdown.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={6 + extraColumns} align="center" sx={{ color: 'text.secondary' }}>
                      {t('reports.noData')}
                    </TableCell>
                  </TableRow>
                )}
                {/* Totals row */}
                {data?.daily_breakdown && data.daily_breakdown.length > 0 && (
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                      {t('reports.cashRegister.total')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {formatCurrency(data?.cash_total || 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {formatCurrency(data?.card_total || 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {formatCurrency(data?.bank_transfer_total || 0)}
                    </TableCell>
                    {hasBankRefunds && (
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {formatCurrency(data?.refund_bank_total || 0)}
                      </TableCell>
                    )}
                    {hasInvoicedElsewhere && (
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {formatCurrency(data?.invoiced_elsewhere_total || 0)}
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {formatCurrency(data?.total_sales || 0)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Cash/card taken by hand against invoices issued earlier (osasuoritus, merkitse
          maksetuksi), by the day the money arrived. Not part of the sales figures above, and
          rendered only when the period has one so the report keeps its usual shape. */}
      {!loading && (data?.later_payments || []).length > 0 && (
        <Paper elevation={0} sx={{ p: 3, mt: 3 }} data-testid="cash-register-later-payments">
          <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
            {t('reports.cashRegister.laterPayments')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('reports.cashRegister.laterPaymentsNote')}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('reports.cashRegister.date')}</TableCell>
                  <TableCell>{t('reports.cashRegister.day')}</TableCell>
                  <TableCell align="right">{t('reports.cashRegister.cash')}</TableCell>
                  <TableCell align="right">{t('reports.cashRegister.card')}</TableCell>
                  <TableCell align="right">{t('reports.cashRegister.total')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.later_payments.map((day) => {
                  const dateParts = day.date.split('-');
                  const displayDate = `${parseInt(dateParts[2])}.${parseInt(dateParts[1])}.${dateParts[0]}`;
                  return (
                    <TableRow key={day.date}>
                      <TableCell>{displayDate}</TableCell>
                      <TableCell>{day.day_name}</TableCell>
                      <TableCell align="right">{formatCurrency(day.cash)}</TableCell>
                      <TableCell align="right">{formatCurrency(day.card)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500 }}>{formatCurrency(day.total)}</TableCell>
                    </TableRow>
                  );
                })}
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                    {t('reports.cashRegister.total')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {formatCurrency(data.later_payments_cash_total || 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {formatCurrency(data.later_payments_card_total || 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {formatCurrency(data.later_payments_total || 0)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}

export default withTranslation()(CashRegisterSection);
